import React, { useContext, useEffect, useState, useRef } from "react";
import Pagereview from "../../components/pageReview/Pagereview";
import ServiceCard from "../../components/cards/serviceCard/ServiceCard";
import List from "../../components/list/List";
import SegmentedTab from "../../components/segmentedTab/SegmentedTab";
import { PaginationRenderer } from "../CMSRenderer/components";
import {
  getServiceCategories,
  getServices,
  bookmarkMyService,
} from "../../utils/apiCalls";
import { FormContext } from "../CMSRenderer/CMSRenderer";

const Services = ({ config }) => {
  const { currentLang } = useContext(FormContext);
  const [view, setView] = useState("grid");
  const [selectedCategory, setSelectedCategory] = useState("G-C");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [serviceData, setServiceData] = useState({
    serviceCategories: [],
    services: [],
    isLoading: true,
    servicesLoading: false,
    myBookmarks: [],
  });
  const checkboxRefs = useRef({});
  const initialLoadRef = useRef(true);
  const isUpdatingUrlRef = useRef(false);
  const searchInputRef = useRef(null);

  const iconMap = {
    "G-C": (
      <img
        src="/attachments/medialib/d52536a8-310e-42c6-a80b-2f90182754b3-1758786348892-863265438-health_individual.svg"
        alt="For Individuals"
      />
    ),
    "G-CP": (
      <img
        src="/attachments/medialib/cb4b7101-9d76-441c-8c24-7ca1e7afdb30-1758786355042-508993476-health_professional.svg"
        alt="For Health Professionals"
      />
    ),
    "G-F": (
      <img
        src="/attachments/medialib/e39fdc0c-6bf3-4481-a976-666baeb4fdb7-1758786360770-729109822-health_facilities.svg"
        alt="For Health Facilities"
      />
    ),
    "G-G": (
      <img
        src="/attachments/medialib/db74e191-a05e-4984-9d0a-ed6c8b6e99eb-1758786342828-536887505-health_gov.svg"
        alt="For Government Entities"
      />
    ),
    "G-B": (
      <img
        src="/attachments/medialib/f0f209f3-2ffe-4c3a-9329-9395c56500ac-1758786330908-355306769-health_businesses.svg"
        alt="For Businesses & Corporates"
      />
    ),
  };

  const filteredServices = serviceData.services.filter((service) => {
    if (!search || search.trim() === "") {
      return true;
    }

    const searchLower = search.toLowerCase().trim();
    const titleEN = service?.title?.toLowerCase() || "";
    const titleAR = service?.titleAR?.toLowerCase() || "";

    return titleEN.includes(searchLower) || titleAR.includes(searchLower);
  });

  useEffect(() => {
    const checkInputValue = () => {
      if (!searchInputRef.current) return;
      const inputElement = searchInputRef.current.querySelector("input");
      if (inputElement) {
        const currentValue = inputElement.value;
        if (currentValue === "" && search !== "") {
          setSearch("");
        }
      }
    };

    const interval = setInterval(checkInputValue, 100);

    return () => clearInterval(interval);
  }, [search]);

  useEffect(() => {
    const setupCloseButtonListener = () => {
      if (!searchInputRef.current) return;

      const closeButton = searchInputRef.current.querySelector(
        ".dda-search-close-icon",
      );
      if (closeButton) {
        const handleCloseClick = () => {
          setSearch("");
          const inputElement = searchInputRef.current.querySelector("input");
          if (inputElement) {
            inputElement.value = "";
          }
        };

        closeButton.addEventListener("click", handleCloseClick);

        return () => {
          closeButton.removeEventListener("click", handleCloseClick);
        };
      }
    };

    const cleanup = setupCloseButtonListener();

    return cleanup;
  }, []);

  useEffect(() => {
    const handleSearchEvents = (e) => {
      const value = e.detail?.value ?? "";

      if (value === "" || value === null) {
        setSearch("");
      } else {
        setSearch(value);
      }
    };

    const searchElement = searchInputRef.current;
    if (searchElement) {
      searchElement.addEventListener("inputChanged", handleSearchEvents);
      searchElement.addEventListener("onChanged", handleSearchEvents);

      return () => {
        searchElement.removeEventListener("inputChanged", handleSearchEvents);
        searchElement.removeEventListener("onChanged", handleSearchEvents);
      };
    }
  }, []);

  const getUrlParams = () => {
    if (typeof window === "undefined") return { type: null };

    const urlParams = new URLSearchParams(window.location.search);
    const type = urlParams.get("type");
    return { type };
  };

  const getData = (categoryType, initialLoad = false) => {
    if (initialLoad) {
      setServiceData((prev) => ({ ...prev, isLoading: true }));
    } else {
      setServiceData((prev) => ({ ...prev, services: [], servicesLoading: true }));
    }

    const fetchData = initialLoad
      ? getServiceCategories().then((res) => {
          return getServices(categoryType).then((res1) => ({
            categories: res?.data || [],
            services: res1?.data?.services || [],
            bookmarks: res1?.data?.myBookmarks || [],
          }));
        })
      : getServices(categoryType).then((res1) => ({
          categories: serviceData.serviceCategories,
          services: res1?.data?.services || [],
          bookmarks: res1?.data?.myBookmarks || [],
        }));

    fetchData
      .then((data) => {
        setServiceData({
          serviceCategories: data.categories,
          services: data.services,
          myBookmarks: data.bookmarks,
          isLoading: false,
          servicesLoading: false,
        });
      })
      .catch((err) => {
        console.error("Error fetching data:", err);
        setServiceData((prev) => ({
          ...prev,
          isLoading: false,
          servicesLoading: false,
        }));
      });
  };

  const updateUrl = (category) => {
    if (isUpdatingUrlRef.current) return;

    isUpdatingUrlRef.current = true;
    const currentUrl = new URL(window.location.href);

    if (category === "G-C") {
      currentUrl.searchParams.delete("type");
    } else {
      currentUrl.searchParams.set("type", category);
    }

    window.history.replaceState(null, "", currentUrl.toString());
    setTimeout(() => {
      isUpdatingUrlRef.current = false;
    }, 100);
  };

  useEffect(() => {
    const { type } = getUrlParams();
    if (type) {
      setSelectedCategory(type);
      getData(type, true);
    } else {
      getData("G-C", true);
    }

    initialLoadRef.current = false;
  }, []);

  useEffect(() => {
    if (!initialLoadRef.current && !serviceData.isLoading) {
      getData(selectedCategory, false);
      updateUrl(selectedCategory);
    }
  }, [selectedCategory]);

  useEffect(() => {
    serviceData?.serviceCategories?.forEach((category) => {
      const checkboxElement = checkboxRefs.current[category.categoryTypeCodeEn];
      if (checkboxElement) {
        const handler = (event) => {
          if (event.detail === true) {
            setSelectedCategory(category.categoryTypeCodeEn);
          }
        };

        checkboxElement.addEventListener("checkboxChange", handler);

        return () => {
          checkboxElement.removeEventListener("checkboxChange", handler);
        };
      }
    });
  }, [serviceData.serviceCategories]);

  useEffect(() => {
    serviceData?.serviceCategories?.forEach((category) => {
      const checkboxElement = checkboxRefs.current[category.categoryTypeCodeEn];
      if (checkboxElement) {
        if (checkboxElement.setAttribute) {
          checkboxElement.setAttribute(
            "checked",
            selectedCategory === category.categoryTypeCodeEn ? "true" : "false",
          );
        }
      }
    });
  }, [selectedCategory, serviceData.serviceCategories]);

  const handleSearchChange = (e) => {
    const value = e.target?.value || "";
    setSearch(value);
  };

  const handleSearchInput = (e) => {
    const value = e.target?.value || "";
    setSearch(value);
  };

  const bookmarkService = async (sid) => {
    console.log("Bookmarked for the session" + sid);
    const bookMarkSerice = await bookmarkMyService(sid);
    getData(selectedCategory, false);
  };

  if (serviceData?.isLoading)
    return (
      <div>
        {currentLang === "en" ? config.loading_text : config.loading_text_ar}
      </div>
    );

  return (
    <>
      <div className="mb-4">
        <img
          className="w-full h-[200px] md:h-[300px] object-cover rounded-xl"
          src={
            config.hero_image ||
            `/attachments/medialib/e7c9bc79-01ae-415e-9f4f-4a12f61c0e48-1759391978364-632391502-23_Services.jpg`
          }
          alt={
            currentLang === "en"
              ? config.hero_image_alt
              : config.hero_image_alt_ar
          }
        />
      </div>
      <h2 className="mb-4 Medium text-primary">
        {currentLang === "en" ? config.page_title : config.page_title_ar}
      </h2>

      <div className="lg:flex gap-6">
        {/* Sidebar */}
        <div className="lg:w-1/5">
          <div className="border rounded-2xl">
            <p className="body Medium p-4">
              {currentLang === "en"
                ? config.sidebar_title
                : config.sidebar_title_ar}
            </p>
            <hr />
            <div className="flex flex-col gap-3 p-4">
              {serviceData?.serviceCategories?.map((category, i) => (
                <dda-checkbox
                  key={i}
                  ref={(el) =>
                    (checkboxRefs.current[category.categoryTypeCodeEn] = el)
                  }
                  title_text={
                    currentLang === "en"
                      ? category?.categoryNameEN
                      : category?.categoryNameAr || ""
                  }
                  checked={
                    selectedCategory === category?.categoryTypeCodeEn
                      ? "true"
                      : "false"
                  }
                  size="md"
                  group_name="categoryFilter"
                  input_id={`category-${category?.categoryTypeCodeEn}`}
                  aria_label={`Filter by ${category?.categoryNameEN}`}
                  style_type="fill"
                  checkbox_status="normal"
                ></dda-checkbox>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:w-4/5">
          <div className="flex flex-wrap items-center gap-3 w-full mb-4 lg:mt-0 mt-4">
            <div className="sm:flex-1">
              <dda-search-input
                ref={searchInputRef}
                placeholder={
                  currentLang === "en"
                    ? config.search_input_placeholder
                    : config.search_input_placeholder_ar
                }
                size="default"
                show_button="false"
                custom_class="w-full"
                helper_text=""
                error_message=""
                validation_id=""
                has_error="false"
                component_mode=""
                button_id="button"
                input_id="Search"
                aria_label="search-input"
                button_aria_label="clear-search"
                search_input_name="search-input"
                close_button_name="close-button"
                search_button_name="search-button"
                onInput={handleSearchInput}
                onChange={handleSearchChange}
              ></dda-search-input>
            </div>

            <SegmentedTab onChange={setView} />
          </div>

          {!!filteredServices && (
            <>
              <p className="body mb-4 text-onSurfaceVariant">
                {currentLang === "en"
                  ? config.service_count_text?.replace(
                      "$count",
                      filteredServices?.length || "0",
                    )
                  : config.service_count_text_ar?.replace(
                      "$count",
                      filteredServices?.length || "0",
                    )}
              </p>
            </>
          )}

          {filteredServices?.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {serviceData.servicesLoading
                ? currentLang === "en"
                  ? config.loading_services_text
                  : config.loading_services_text_ar
                : search
                  ? currentLang === "en"
                    ? config.no_search_results_text
                    : config.no_search_results_text_ar
                  : currentLang === "en"
                    ? config.no_services_text
                    : config.no_services_text_ar}
            </div>
          ) : view === "grid" ? (
            <div
              className={`grid sm:grid-cols-2 gap-5 auto-rows-fr ${serviceData.servicesLoading ? "opacity-50 pointer-events-none" : ""}`}
            >
              {filteredServices?.map((service, index) => (
                <ServiceCard
                  key={service.guid || index}
                  icon={
                    iconMap[service.customerSegmentationType] || iconMap["G-C"]
                  }
                  title={
                    currentLang === "en"
                      ? service?.title
                      : service?.titleAR || ""
                  }
                  description={
                    currentLang === "en"
                      ? service?.serviceDescriptionEN
                      : service?.serviceDescriptionAR || ""
                  }
                  onViewDetails={() =>
                    (window.location.href =
                      "/services/serviceDetails/" + service.guid)
                  }
                  onApply={() => (window.location.href = service.applyNowLink)}
                  onBookmark={() => {
                    if (!!window.sessionName) {
                      bookmarkService(service.guid);
                    } else {
                      setShowModal(true);
                    }
                  }}
                  bookmarked={serviceData.myBookmarks.includes(service.guid)}
                  showApplyButton={!!service.applyNowLink}
                />
              ))}
            </div>
          ) : (
            <div
              className={`mt-4 *:mb-3 ${serviceData.servicesLoading ? "opacity-50 pointer-events-none" : ""}`}
            >
              {filteredServices?.map((service, index) => (
                <List
                  key={service.guid || index}
                  icon={
                    iconMap[service.customerSegmentationType] || iconMap["G-C"]
                  }
                  title={
                    currentLang === "en"
                      ? service.title
                      : service.titleAR || service.title
                  }
                  subtitle={
                    currentLang === "en"
                      ? service.serviceDescriptionEN
                      : service.serviceDescriptionAR || ""
                  }
                  onViewDetails={() =>
                    (window.location.href =
                      "/services/serviceDetails/" + service.guid)
                  }
                  onApply={() => (window.location.href = service.applyNowLink)}
                  onBookmark={() => {
                    if (!!window.sessionName) {
                      bookmarkService(service.guid);
                    } else {
                      setShowModal(true);
                    }
                  }}
                  bookmarked={false}
                  showApplyButton={!!service.applyNowLink}
                />
              ))}
            </div>
          )}

          {showModal && (
            <div className="fixed inset-0 z-[99999] flex items-center justify-center">
              {/* Backdrop */}
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setShowModal(false)}
              />

              {/* Modal */}
              <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl p-6 mx-4">
                <div className="max-h-[60vh] overflow-auto">
                  <h4 className="font-semibold mb-4 text-primary">
                    {currentLang === "en"
                      ? "Login to Continue."
                      : "سجّل الدخول للمتابعة."}
                  </h4>

                  <div className="mb-6">
                    <p>
                      {currentLang === "en"
                        ? "Please Login to add or manage your bookmarks."
                        : "يرجى تسجيل الدخول لإضافة أو إدارة إشاراتك المرجعية."}
                    </p>
                  </div>
                </div>

                <hr className="mb-3" />
                <div className="flex justify-end gap-2">
                  <dda-button
                    button_color="default-tertiary"
                    start_icon=""
                    button_text={currentLang === "en" ? "Close" : "أغلق"}
                    end_icon=""
                    custom_class=""
                    component_mode=""
                    button_id="button"
                    aria_label="button"
                    onclick={() => {
                      setShowModal(false);
                    }}
                    button_name="button"
                    class="hydrated"
                  ></dda-button>
                  
                  <dda-button
                    button_color="default-primary"
                    start_icon=""
                    button_text={currentLang === "en" ? "Login" : "دخول"}
                    end_icon=""
                    custom_class=""
                    component_mode=""
                    button_id="button"
                    aria_label="button"
                    onclick={() => {
                      window.location.href = "/Login/Auth";
                    }}
                    button_name="button"
                    class="hydrated"
                  ></dda-button>
                </div>
              </div>
            </div>
          )}

          {/* <dda-pagination
            total_pages="8"
            current_page="1"
            type="text"
            custom_class="my-4"
          ></dda-pagination> */}
          {/* <PaginationRenderer /> */}
        </div>
      </div>
    </>
  );
};

export default Services;
